import './styles.css'
import type {
  PublicCatalog,
  Availability,
  CreateAppointmentRequestInput,
} from '@mencar/shared'

const API = import.meta.env.VITE_API_BASE ?? ''
const SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY ?? ''

// --- DOM ---
const form = document.getElementById('form') as HTMLFormElement
const successEl = document.getElementById('success') as HTMLElement
const errorEl = document.getElementById('error') as HTMLParagraphElement
const submitBtn = document.getElementById('submit') as HTMLButtonElement
const dateInput = document.getElementById('date') as HTMLInputElement
const timeSelect = document.getElementById('time') as HTMLSelectElement
const servicesField = document.getElementById('servicesField') as HTMLElement
const servicesEl = document.getElementById('services') as HTMLElement
const turnstileHolder = document.getElementById('turnstile-holder') as HTMLElement

const money = (n: number | null): string =>
  n == null ? '' : new Intl.NumberFormat('tr-TR').format(n) + ' ₺'

function todayLocal(): string {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function showError(msg: string): void {
  errorEl.textContent = msg
  errorEl.hidden = false
}
function clearError(): void {
  errorEl.hidden = true
}

// Saat <select>'ini güvenli DOM ile doldurur (innerHTML/interpolasyon yok).
function setTimeOptions(opts: Array<{ value: string; label: string }>): void {
  timeSelect.replaceChildren(
    ...opts.map(({ value, label }) => {
      const o = document.createElement('option')
      o.value = value
      o.textContent = label
      return o
    }),
  )
}

// --- Turnstile ---
function initTurnstile(): void {
  if (!SITEKEY) return
  const widget = document.createElement('div')
  widget.className = 'cf-turnstile'
  widget.dataset.sitekey = SITEKEY
  widget.dataset.theme = 'auto'
  turnstileHolder.appendChild(widget)

  const script = document.createElement('script')
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
  script.async = true
  script.defer = true
  document.head.appendChild(script)
}

function turnstileToken(): string {
  if (!SITEKEY) return 'dev' // API tarafında secret yoksa doğrulama atlanır
  const w = window as unknown as { turnstile?: { getResponse?: () => string } }
  if (w.turnstile?.getResponse) return w.turnstile.getResponse() ?? ''
  const input = document.querySelector<HTMLInputElement>('[name="cf-turnstile-response"]')
  return input?.value ?? ''
}

function resetTurnstile(): void {
  const w = window as unknown as { turnstile?: { reset?: () => void } }
  w.turnstile?.reset?.()
}

// --- Hizmet katalogu ---
async function loadServices(): Promise<void> {
  try {
    const res = await fetch(`${API}/public/services`)
    if (!res.ok) return
    const catalog = (await res.json()) as PublicCatalog
    if (catalog.length === 0) return

    servicesEl.innerHTML = ''
    for (const cat of catalog) {
      const catLabel = document.createElement('div')
      catLabel.className = 'svc-cat'
      catLabel.textContent = cat.name
      servicesEl.appendChild(catLabel)

      for (const svc of cat.services) {
        const row = document.createElement('label')
        row.className = 'svc'
        const cb = document.createElement('input')
        cb.type = 'checkbox'
        cb.value = svc.id
        cb.name = 'service'
        const name = document.createElement('span')
        name.className = 'name'
        name.textContent = svc.name
        const price = document.createElement('span')
        price.className = 'price'
        price.textContent = money(svc.defaultPrice)
        row.append(cb, name, price)
        servicesEl.appendChild(row)
      }
    }
    servicesField.hidden = false
  } catch {
    // Katalog yüklenemezse hizmet seçimi olmadan devam edilebilir.
  }
}

// --- Müsaitlik (dolu saatleri gizle) ---
async function loadAvailability(date: string): Promise<void> {
  timeSelect.disabled = true
  setTimeOptions([{ value: '', label: 'Yükleniyor…' }])
  try {
    const res = await fetch(`${API}/public/availability?date=${encodeURIComponent(date)}`)
    if (!res.ok) throw new Error('availability')
    const data = (await res.json()) as Availability
    const free = data.slots.filter((s) => s.available)
    if (free.length === 0) {
      setTimeOptions([{ value: '', label: 'Bu gün için uygun saat yok' }])
      return
    }
    setTimeOptions([
      { value: '', label: 'Saat seçin' },
      ...free.map((s) => ({ value: s.time, label: s.time })),
    ])
    timeSelect.disabled = false
  } catch {
    setTimeOptions([{ value: '', label: 'Saatler yüklenemedi' }])
  }
}

// --- Gönderim ---
async function submit(e: SubmitEvent): Promise<void> {
  e.preventDefault()
  clearError()

  const fullName = (form.elements.namedItem('fullName') as HTMLInputElement).value.trim()
  const phone = (form.elements.namedItem('phone') as HTMLInputElement).value.trim()
  const plate = (form.elements.namedItem('plate') as HTMLInputElement).value.trim()
  const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value.trim()
  const date = dateInput.value
  const time = timeSelect.value
  const serviceIds = [...servicesEl.querySelectorAll<HTMLInputElement>('input[name="service"]:checked')].map((c) => c.value)

  if (!fullName) return showError('Lütfen ad soyad girin.')
  if (!date) return showError('Lütfen tarih seçin.')
  if (!time) return showError('Lütfen uygun bir saat seçin.')

  const token = turnstileToken()
  if (SITEKEY && !token) return showError('Lütfen doğrulamayı tamamlayın.')

  const payload: CreateAppointmentRequestInput = {
    fullName,
    ...(phone ? { phone } : {}),
    ...(plate ? { plate } : {}),
    serviceIds,
    date,
    time,
    ...(message ? { message } : {}),
    turnstileToken: token,
  }

  submitBtn.disabled = true
  submitBtn.textContent = 'Gönderiliyor…'
  try {
    const res = await fetch(`${API}/public/appointments`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res.status === 201) {
      form.hidden = true
      successEl.hidden = false
      return
    }
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    showError(body.error ?? 'Randevu oluşturulamadı, lütfen tekrar deneyin.')
    resetTurnstile()
  } catch {
    showError('Bağlantı hatası. Lütfen tekrar deneyin.')
    resetTurnstile()
  } finally {
    submitBtn.disabled = false
    submitBtn.textContent = 'Randevu Oluştur'
  }
}

// --- Init ---
dateInput.min = todayLocal()
dateInput.addEventListener('change', () => {
  if (dateInput.value) void loadAvailability(dateInput.value)
})
form.addEventListener('submit', submit)
document.getElementById('again')?.addEventListener('click', () => {
  form.reset()
  form.hidden = false
  successEl.hidden = true
  timeSelect.disabled = true
  setTimeOptions([{ value: '', label: 'Önce tarih seçin' }])
  clearError()
  resetTurnstile()
})

initTurnstile()
void loadServices()
